import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Globals } from '../global/globals';

export interface SecondStagePaper {
  id: number; // maps to paperToProject ID
  paperId: number; // the underlying Paper id (needed to materialize a remote PDF)
  title: string;
  authors: string;
  pdfId: number | null;
  pdfUrl: string | null;
}

export interface ResearcherProgress {
  userId: number;
  firstName: string;
  lastName: string;
  role: string;
  completedCount: number;
  totalCount: number;
}

export interface SecondStageScreeningStats {
  totalPapers: number;
  unscreenedPapers: number;
  partiallyScreenedPapers: number;
  includedPapers: number;
  excludedPapers: number;
  /** Only present for PI; null/undefined for regular researchers. */
  teamTotalDecisions?: number;
  teamTotalRequiredDecisions?: number;
  teamIncludedPapers?: number;
  teamExcludedPapers?: number;
  researcherProgress?: ResearcherProgress[];
}

export interface SecondStageDecision {
  screenerName: string;
  includeDecision: boolean;
  exclusionReason: string | null;
}

export interface SecondStageConflict {
  paperToProjectId: number;
  paperId: number;
  title: string;
  authors: string;
  doi: string | null;
  decision1: SecondStageDecision;
  decision2: SecondStageDecision;
  pdfId: number | null;
  pdfUrl: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SecondStageScreeningService {
  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  private getBaseUri(projectId: string): string {
    return `${this.globals.backendUri}/projects/${projectId}/second-stage-screening`;
  }

  getNextPaper(projectId: string): Observable<SecondStagePaper | null> {
    return this.httpClient.get<SecondStagePaper | null>(`${this.getBaseUri(projectId)}/next`);
  }

  getCurrentPaper(projectId: string): Observable<SecondStagePaper | null> {
    return this.httpClient.get<SecondStagePaper | null>(`${this.getBaseUri(projectId)}/current`);
  }

  submitVote(
    projectId: string,
    paperId: number,
    include: boolean,
    exclusionReason?: string,
  ): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/vote`, {
      include,
      exclusionReason,
    });
  }

  getStats(projectId: string): Observable<SecondStageScreeningStats> {
    return this.httpClient.get<SecondStageScreeningStats>(`${this.getBaseUri(projectId)}/stats`);
  }

  unlockPaper(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/unlock`, {});
  }

  skipPaper(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/skip`, {});
  }

  getConflicts(projectId: string): Observable<SecondStageConflict[]> {
    return this.httpClient.get<SecondStageConflict[]>(`${this.getBaseUri(projectId)}/conflicts`);
  }

  resolveConflict(
    projectId: string,
    paperId: number,
    include: boolean,
    exclusionReason?: string,
  ): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/resolve`, {
      include,
      exclusionReason,
    });
  }

  deleteVote(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.getBaseUri(projectId)}/${paperId}/vote`);
  }

  advanceToDataExtraction(projectId: string): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/advance`, {});
  }
}
